/**
 * Request Queue Manager
 *
 * Manages queuing of failed API requests during token refresh.
 * Automatically retries queued requests after successful token refresh.
 *
 * Key Features:
 * - Stores both fetch RequestInit AND hey-api RequestOptions per request
 * - Preserves request body streams via cloning
 * - Maintains original response format preferences (responseStyle)
 * - Automatic timeout (30s) for queued requests
 */

import type { RequestOptions } from "@/proofingService-client/client/types.gen";

interface QueuedRequest {
  request: Request;
  options: RequestInit;
  opts?: Partial<RequestOptions>; // hey-api request options for response parsing
  resolve: (value: Response) => void;
  reject: (error: unknown) => void;
  timestamp: number;
}

/**
 * Singleton service for managing request queue during token refresh
 */
class RequestQueue {
  private queue: QueuedRequest[] = [];
  private readonly DEFAULT_TIMEOUT_MS = 30000; // 30 seconds

  /**
   * Add a failed request to the queue
   *
   * Stores both the fetch options AND hey-api RequestOptions to ensure
   * proper retry behavior with correct response format.
   *
   * @param request - The original Request object
   * @param options - The fetch request options (method, headers, body, credentials)
   * @param opts - The hey-api request options (includes responseStyle for response parsing)
   * @returns Promise that resolves when the request is retried
   *
   * @example
   * ```typescript
   * requestQueue.enqueue(
   *   request,
   *   { method: 'POST', body: clonedBody },
   *   { responseStyle: 'data' } // Ensures response is parsed correctly
   * );
   * ```
   */
  enqueue(
    request: Request,
    options: RequestInit,
    opts?: Partial<RequestOptions>,
  ): Promise<Response> {
    return new Promise((resolve, reject) => {
      const queuedRequest: QueuedRequest = {
        request,
        options,
        opts,
        resolve,
        reject,
        timestamp: Date.now(),
      };

      this.queue.push(queuedRequest);

      // Set up timeout
      setTimeout(() => {
        this.timeoutRequest(queuedRequest);
      }, this.DEFAULT_TIMEOUT_MS);
    });
  }

  /**
   * Get all queued requests
   * @returns Array of queued requests
   */
  getAll(): QueuedRequest[] {
    return [...this.queue];
  }

  /**
   * Clear all queued requests
   * Optionally reject them with an error
   *
   * @param error - Optional error to reject all requests with
   */
  clear(error?: unknown): void {
    if (error) {
      // Reject all pending requests
      for (const queuedRequest of this.queue) {
        queuedRequest.reject(error);
      }
    }

    this.queue = [];
  }

  /**
   * Remove a specific request from the queue
   * @private
   */
  private remove(queuedRequest: QueuedRequest): void {
    const index = this.queue.indexOf(queuedRequest);
    if (index > -1) {
      this.queue.splice(index, 1);
    }
  }

  /**
   * Handle timeout for a queued request
   * @private
   */
  private timeoutRequest(queuedRequest: QueuedRequest): void {
    // Check if request is still in queue
    if (!this.queue.includes(queuedRequest)) {
      return; // Already processed
    }

    this.remove(queuedRequest);
    queuedRequest.reject(
      new Error(`Request timeout: Token refresh took longer than ${this.DEFAULT_TIMEOUT_MS}ms`),
    );
  }

  /**
   * Get the current queue size
   * @returns Number of requests in the queue
   */
  size(): number {
    return this.queue.length;
  }
}

// Export singleton instance
export const requestQueue = new RequestQueue();
export type { QueuedRequest };
